import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { FeedbackMode } from './p-feedback-mode';
import { AnimationItem } from 'lottie-web';
import { DefaultTo } from '@panaya/utils';

const ANIMATION_PATH = 'assets/animations/like-animation.json';
const GRAY_ANIMATION_PATH = 'assets/animations/like-animation-gray.json';

@Component({
    selector: 'p-like-dislike',
    template: `
        <div class="like-dislike">
            <div class="btn up" [attr.automation-id]="'p-like-dislike_up'" [matTooltip]="tooltipLike">
                <ng-lottie [options]="options" (animationCreated)="onAnimate($event)" (click)="play()"> </ng-lottie>
            </div>
            <div class="btn" [attr.automation-id]="'p-like-dislike_down'" [matTooltip]="tooltipDislike">
                <p-button
                    iconBefore="thumbs-down"
                    [class]="dislikeButtonClass"
                    [buttonType]="buttonType.IsIcon"
                    (buttonClicked)="onFeedbackClicked(feedbackModeEnum.DISLIKE)"
                ></p-button>
            </div>
        </div>
    `,
    styleUrls: ['./p-like-dislike.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PLikeDislikeComponent implements OnInit {
    @Input() @DefaultTo('Helpful') tooltipLike!: string;
    @Input() @DefaultTo('Needs improvement') tooltipDislike!: string;
    _feedbackMode = FeedbackMode.CANCEL;
    @Input() set feedbackMode(feedbackStatus: FeedbackMode) {
        this.setButtonsClasses(feedbackStatus);
        this._feedbackMode = feedbackStatus;
        this.updateAnimationPath();
        if (this.feedbackMode === FeedbackMode.LIKE && this.animation && this.animation.isPaused) {
            this.animation.goToAndStop(40, true);
        }
        if (this.feedbackMode !== FeedbackMode.LIKE && this.animation && this.animation.isPaused) {
            this.animation.stop();
        }
    }

    get feedbackMode(): FeedbackMode {
        return this._feedbackMode;
    }

    @Output() feedbackClicked = new EventEmitter<FeedbackMode>();

    constructor() {
        this.options = {
            path: this.feedbackMode == FeedbackMode.DISLIKE ? GRAY_ANIMATION_PATH : ANIMATION_PATH,
            autoplay: false,
            loop: false
        };
    }

    dislikeButtonClass = 'mode-cancel';

    buttonType = ButtonTypeEnum;
    feedbackModeEnum = FeedbackMode;
    animation: AnimationItem | undefined;

    options: {
        path: string;
        loop: boolean;
        autoplay: boolean;
    };

    ngOnInit(): void {
        this.options = {
            path: this.feedbackMode == FeedbackMode.DISLIKE ? GRAY_ANIMATION_PATH : ANIMATION_PATH,
            autoplay: false,
            loop: false
        };
    }

    onAnimate(animationItem: AnimationItem): void {
        this.animation = animationItem;
        if (this.feedbackMode === FeedbackMode.LIKE) {
            this.animation.goToAndStop(40, true);
        }
    }

    updateAnimationPath(): void {
        const path = this.feedbackMode === FeedbackMode.DISLIKE ? GRAY_ANIMATION_PATH : ANIMATION_PATH;
        if (path !== this.options.path && this.animation?.isPaused) {
            this.options = {
                ...this.options,
                path: path
            };
        }
    }

    play(): void {
        if (this.animation) {
            if (this.feedbackMode !== FeedbackMode.LIKE) {
                this.animation.goToAndPlay(0, true);
            } else {
                this.animation.stop();
            }
        }
        this.onFeedbackClicked(FeedbackMode.LIKE);
    }

    private setButtonsClasses(feedbackStatus: FeedbackMode): void {
        switch (feedbackStatus) {
            case FeedbackMode.LIKE:
                this.dislikeButtonClass = 'mode-unselected';
                break;
            case FeedbackMode.DISLIKE:
                this.dislikeButtonClass = 'mode-dislike';
                break;
            case FeedbackMode.CANCEL:
                this.dislikeButtonClass = 'mode-cancel';
                break;
        }
    }

    onFeedbackClicked(feedback: FeedbackMode): void {
        if (this.feedbackMode === feedback) {
            this.feedbackMode = FeedbackMode.CANCEL;
        } else {
            this.feedbackMode = feedback;
        }
        this.updateAnimationPath();
        this.feedbackClicked.emit(this.feedbackMode);
    }
}
